/**
 * HisniModule — minimal native module implementing the PreviewableAweniModule
 * contract from `src/modules/types.ts`.
 *
 * v0 (this file):
 *   - `preview()` returns a real `PreviewSnapshot` built from existing
 *     `prayerTimeService.getNextPrayer()` + `getCurrentSpecialDay()`.
 *     Cheap (<5 ms), synchronous-friendly, cache-tolerant.
 *   - `mount()` defers to the existing /memoriz/ iframe path. The full
 *     native rebuild of the Memoriz UI is C2 (multi-day Codex work) and
 *     out of scope for v0. v0's job is to populate the AWENI home Today
 *     zone with real data, not to replace the iframe yet.
 *
 * v1 (Codex C2):
 *   - Replace `_mountIframe()` with native Quran reader + memorize flow
 *     ported from Apps/Memoriz/frontend/.
 *   - Wire the `mz_attributions` UI lines per ST1 audit.
 */

import type {
  PreviewableAweniModule,
  PreviewSnapshot,
  MountedInstance,
  ModuleRoute,
} from '../types';
import type { AweniContext } from '../../types';
import { prayerTimeService } from '../../services/PrayerTimeService';
import { getCurrentSpecialDay } from '../../services/IslamicCalendar';
import { gregorianToHijri, formatHijri } from '../../services/HijriDate';
// NativeSRSView import removed 2026-05-14 (v3 pivot) — Hisni IS Memoriz now.

const ROUTES: ModuleRoute[] = [
  // 2026-05-08: /hisni is now the canonical primary route (per ST3 rename).
  // /quran + /quran/memorize remain as back-compat aliases for any deep
  // links / bookmarks / native push notifications already in the wild.
  // The router's MODULE_MAP maps all three to the same `memoriz` module.
  { path: '/hisni', primary: true },
  { path: '/quran' },
  { path: '/quran/memorize' },
];

let mountSeq = 0;
const SNAPSHOT_CACHE_KEY = 'aweni_preview_memoriz';

export class HisniModule implements PreviewableAweniModule {
  readonly id = 'memoriz';
  readonly routes = ROUTES;
  private container: HTMLElement | null = null;
  private currentMountId: string | null = null;
  private nativeUnmount: (() => void) | null = null;

  // -------------------------------------------------------------------------
  // mount() — for now delegates to the existing /memoriz/ iframe path.
  // Triggered when the user navigates to #/quran. The shell already iframes
  // /memoriz/ via registry.ts loadStrategy: 'iframe', so this method is
  // present to satisfy the contract; the existing iframe-based path stays
  // active until C2 ships the native rebuild.
  // -------------------------------------------------------------------------
  async mount(container: HTMLElement, _ctx: AweniContext): Promise<void> {
    this.container = container;
    this.currentMountId = `hisni_${++mountSeq}`;
    // 2026-05-14 (v3 pivot): Hisni IS Memoriz. No native landing in AWENI.
    // #/hisni redirects to /memoriz/ directly — Memoriz IS the Hisni surface.
    // Phase 2 features (SurahBrowser, StatsView, etc.) go INTO Memoriz, not
    // here. AWENI top bar disappears; Memoriz top bar shows "Back to AWENI"
    // link so the user can return.
    // Show a brief loading skeleton during the redirect (avoids flash).
    container.innerHTML = `
      <div style="display:flex;align-items:center;justify-content:center;min-height:60vh;color:var(--ln-text-secondary,#666);font-size:.9rem">
        <div style="text-align:center">
          <div style="font-size:2rem;margin-bottom:8px">🕌</div>
          <div>Opening Hisni…</div>
        </div>
      </div>`;
    // window.location.replace avoids polluting the back-stack — user's "Back"
    // button returns them to wherever they came from BEFORE the AWENI shell,
    // not to a half-loaded Hisni placeholder.
    window.location.replace('/hisni/#/start');
  }

  unmount(): void {
    if (this.nativeUnmount) {
      this.nativeUnmount();
      this.nativeUnmount = null;
    }
    this.container = null;
    this.currentMountId = null;
  }

  destroy(_instance: MountedInstance): void {
    this.unmount();
  }

  // -------------------------------------------------------------------------
  // preview() — fast, cached, robust.
  // -------------------------------------------------------------------------
  async preview(ctx: AweniContext): Promise<PreviewSnapshot> {
    try {
      const t = ctx.i18n.t.bind(ctx.i18n);
      const locale = ctx.i18n.locale;

      // Primary metric: next prayer countdown (existing service, cached).
      let primaryLabel = t('hisni_preview_today') || 'Today';
      let primaryValue = t('hisni_preview_quran_companion') || 'Quran companion';
      let bgVariant: PreviewSnapshot['bgVariant'] = 'neutral';

      try {
        const next = prayerTimeService.getNextPrayer();
        if (next) {
          const mins = Math.max(0, Math.round(next.minutesUntil));
          const prayerName = t(`prayer_${next.name.toLowerCase()}`) || next.name;
          if (mins < 60) {
            primaryLabel = prayerName;
            primaryValue = `${t('prayer_in') || 'in'} ${mins} ${t('prayer_min_short') || 'min'}`;
            if (mins <= 15) bgVariant = 'attention';
          } else {
            const h = Math.floor(mins / 60);
            const m = mins % 60;
            primaryLabel = prayerName;
            primaryValue = `${t('prayer_in') || 'in'} ${h}${t('prayer_hr_short') || 'h'} ${m}${t('prayer_min_short') || 'm'}`;
          }
        }
      } catch {
        // Prayer service not ready / no location — keep generic label.
      }

      // Secondary metric: special day OR Hijri date.
      let secondaryLabel: string | undefined;
      let secondaryValue: string | undefined;
      try {
        const sd = getCurrentSpecialDay();
        if (sd && sd.isToday) {
          const labelKey = `holiday_${sd.id}`;
          const label = t(labelKey);
          if (label && label !== labelKey) {
            secondaryLabel = '';
            secondaryValue = label;
            bgVariant = 'success';
          }
        }
        if (!secondaryValue) {
          const h = gregorianToHijri();
          secondaryLabel = '';
          secondaryValue = formatHijri(h, locale);
        }
      } catch {
        // Hijri service issue — skip secondary
      }

      const snapshot: PreviewSnapshot = {
        primaryMetric: { label: primaryLabel, value: primaryValue },
        secondaryMetric: secondaryValue
          ? { label: secondaryLabel || '', value: secondaryValue }
          : undefined,
        ctaLabel: t('hisni_preview_open') || 'Open Hisni',
        ctaRoute: '#/hisni',
        iconName: 'mosque',
        ariaLabel: `Hisni — ${primaryLabel} ${primaryValue}${secondaryValue ? ', ' + secondaryValue : ''}`,
        bgVariant,
        computedAt: new Date().toISOString(),
      };

      // Persist cache so the home renders the latest snapshot synchronously next visit.
      try { localStorage.setItem(SNAPSHOT_CACHE_KEY, JSON.stringify(snapshot)); }
      catch { /* quota / SSR — ignore */ }

      return snapshot;
    } catch (err) {
      return {
        primaryMetric: { label: '', value: '' },
        ctaLabel: ctx.i18n.t('hisni_preview_open') || 'Open Hisni',
        ctaRoute: '#/hisni',
        iconName: 'mosque',
        ariaLabel: 'Hisni',
        errorState: { message: ctx.i18n.t('preview_tap_to_open') || 'Tap to open', recoveryRoute: '#/hisni' },
        computedAt: new Date().toISOString(),
      };
    }
  }
}

export default new HisniModule();
